import { KatButton, KatModal } from '@amzn/katal-react';
import React, { FC, useState, useEffect } from 'react';
import { ConfirmationDialog } from 'src/components/general/ConfirmationDialog';
import styles from './CampaignSuccessModal.module.css';
import { TranslatableConstants } from 'src/constants';
import { CMS } from '@amzn/fire-tv-campaign-automation-service';
import { logger } from 'src/logger';

/**
 * Interface for CampaignSuccessModal component props
 * @interface
 * @property {boolean} isVisible - Controls the visibility of the modal
 * @property {string} campaignCmsUrl - URL of the created campaign in the CMS
 * @property {() => void} onClose - Callback function executed when the modal is closed
 * @property {CMS} cms - The CMS type where the campaign was created (used in confirmation message)
 */
export interface CampaignSuccessModalProps {
  isVisible: boolean;
  campaignCmsUrl: string;
  onClose: () => void;
  cms: CMS;
}

/**
 * Modal displayed after a campaign is successfully created
 *
 * This component shows a success message with the campaign URL and provides options to:
 * - Copy the campaign URL to clipboard
 * - Navigate directly to the campaign (Yes button)
 * - Close the modal and return to the application (No button with confirmation)
 *
 * When choosing to close the modal, a confirmation dialog appears to ensure the user
 * has saved or copied the campaign URL before proceeding.
 *
 * @param {CampaignSuccessModalProps} props - Component properties
 * @returns {React.ReactElement} - Rendered component
 */
export const CampaignSuccessModal: FC<CampaignSuccessModalProps> = ({
  isVisible,
  campaignCmsUrl,
  onClose,
  cms,
}) => {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [timeoutId, setTimeoutId] = useState<number | null>(null);

  /**
   * Cleanup effect to clear timeout on component unmount
   * Prevents memory leaks and updating state on unmounted component
   */
  useEffect(() => {
    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  /**
   * Helper function to set a new timeout after clearing any existing one
   * @param status - The status to set after the timeout
   * @param delay - The delay in milliseconds before resetting to idle
   */
  const setStatusWithTimeout = (status: 'idle' | 'success' | 'error', delay: number) => {
    setCopyStatus(status);
    // Clear any existing timeout
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    // Set new timeout and store its ID
    const newTimeoutId = window.setTimeout(() => setCopyStatus('idle'), delay);
    setTimeoutId(newTimeoutId);
  };

  /**
   * Copies the campaign URL to clipboard and provides user feedback
   */
  const handleCopyUrl = () => {
    // Check if clipboard API is available (important for testing environments)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(campaignCmsUrl)
        .then(() => {
          logger.debug('URL copied to clipboard');
          setStatusWithTimeout('success', 2000);
        })
        .catch((error) => {
          logger.error('Failed to copy URL: ', error);
          setStatusWithTimeout('error', 3000);
        });
    } else {
      logger.warn('Clipboard API not available');
      setStatusWithTimeout('error', 3000);
    }
  };

  /**
   * Navigates to the campaign URL in the browser
   */
  const handleViewCampaign = () => {
    // Use assign instead of setting href directly for better testability
    window.location.assign(campaignCmsUrl);
  };

  /**
   * Shows the confirmation dialog when 'No' button is clicked
   */
  const handleSuccessModalNoClick = () => {
    setShowConfirmationDialog(true);
  };

  /**
   * Handles confirmation dialog being confirmed
   * Closes the modal, triggers the onClose callback, and reloads the page
   */
  const handleConfirmationModalClose = () => {
    setShowConfirmationDialog(false);
    onClose();
    window.location.reload();
  };

  /**
   * Closes the confirmation dialog without further action when canceled
   */
  const handleConfirmationModalCancelClose = () => {
    setShowConfirmationDialog(false);
  };

  /**
   * Handles modal close event (X button or escape key)
   * Prevents default close behavior and shows confirmation dialog instead
   * @param {KatModal.CloseEvent} event - Modal close event
   */
  const handleSuccessModalClose = (event: KatModal.CloseEvent) => {
    event.preventDefault();
    setShowConfirmationDialog(true);
  };

  return (
    <>
      <KatModal
        visible={isVisible}
        onClose={handleSuccessModalClose}
        data-testid="campaign-success-modal"
      >
        <span slot="title">{TranslatableConstants.CAMPAIGN_CREATION_SUCCESS}</span>
        <div className={styles.successModalContent}>
          <p>{TranslatableConstants.CAMPAIGN_SUCCESS_FOLLOW_UP}</p>
          <div className={styles.urlContainer}>
            <span className={styles.url} aria-label="Campaign URL">
              {campaignCmsUrl}
            </span>
            <div>
              <KatButton
                variant="secondary"
                label="Copy URL"
                onClick={handleCopyUrl}
                data-testid="copy-url-button"
              />
              {copyStatus === 'success' && (
                <span className={styles.successText} data-testid="copy-success">
                  ✓ Copied!
                </span>
              )}
              {copyStatus === 'error' && (
                <span className={styles.errorText} data-testid="copy-error">
                  Failed to copy
                </span>
              )}
            </div>
          </div>
          <p>{TranslatableConstants.CONFIRM_VIEW_CAMPAIGN}</p>
          <div className={styles.actionButtons}>
            <KatButton
              variant="secondary"
              label="No"
              onClick={handleSuccessModalNoClick}
              data-testid="view-campaign-no"
            />
            <KatButton
              variant="primary"
              label="Yes"
              onClick={handleViewCampaign}
              data-testid="view-campaign-yes"
            />
          </div>
        </div>
      </KatModal>

      <ConfirmationDialog
        isVisible={showConfirmationDialog}
        title="Campaign URL"
        message={TranslatableConstants.CONFIRM_DIALOG_CLOSE_MESSAGE(cms)}
        confirmText={TranslatableConstants.CONFIRM_TEXT}
        cancelText={TranslatableConstants.CANCEL_TEXT}
        onConfirm={handleConfirmationModalClose}
        onCancel={handleConfirmationModalCancelClose}
        data-testid="url-confirmation-dialog"
      />
    </>
  );
};
